import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MapPin, Phone, Mail, Clock, Send, MessageSquare, 
  Building, ArrowRight, CheckCircle, Loader2
} from 'lucide-react';

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSubmitted(true);
    toast.success('Messaggio inviato con successo!');
    setLoading(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Sede',
      content: 'Via Roma 123',
      subtext: '00100 Roma, Italia'
    },
    {
      icon: Phone,
      title: 'Telefono',
      content: '+39 06 1234567',
      subtext: 'Lun-Ven 9:00-18:00'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@weqasa.it',
      subtext: 'Rispondiamo entro 24h'
    },
    {
      icon: Clock,
      title: 'Orari',
      content: 'Lun - Ven: 9:00 - 18:00',
      subtext: 'Sabato: 9:00 - 13:00'
    }
  ];

  const faqs = [
    {
      question: 'Come posso partecipare a una Gara di Vendita?',
      answer: 'Registrati come acquirente, trova l\'immobile che ti interessa e richiedi la partecipazione. Dopo la verifica dei documenti, sarai abilitato.'
    },
    {
      question: 'Quali documenti servono per vendere?',
      answer: 'Servono visura catastale, planimetria, certificato APE e atto di provenienza. Ti guideremo passo dopo passo nel caricamento.'
    },
    {
      question: 'Come vengono calcolati i prezzi base?',
      answer: 'I prezzi base sono calcolati utilizzando i dati ufficiali dell\'Osservatorio del Mercato Immobiliare (OMI).'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-[#F5F5F7] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0071e3] mb-6">
            <MessageSquare className="h-8 w-8 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1D1D1F] tracking-tight mb-6">
            Come possiamo aiutarti?
          </h1>
          <p className="text-xl text-[#86868B] max-w-2xl mx-auto">
            Siamo qui per rispondere a tutte le tue domande su WEQASA e le Gare di Vendita immobiliari.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mb-4">
                  <info.icon className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-medium text-[#86868B] mb-1">{info.title}</h3>
                <p className="text-lg font-semibold text-[#1D1D1F]">{info.content}</p>
                <p className="text-sm text-[#86868B]">{info.subtext}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight mb-2">
                Inviaci un messaggio
              </h2>
              <p className="text-[#86868B] mb-8">
                Compila il form e ti risponderemo entro 24 ore lavorative.
              </p>

              {submitted ? (
                <div className="bg-[#F5F5F7] rounded-3xl p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
                    Messaggio Inviato!
                  </h3>
                  <p className="text-[#86868B] mb-6">
                    Grazie per averci contattato. Ti risponderemo al più presto.
                  </p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                    }}
                    variant="outline"
                    className="rounded-full"
                  >
                    Invia un altro messaggio
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1D1D1F]">Nome e Cognome *</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Mario Rossi"
                        value={formData.name}
                        onChange={handleChange}
                        className="h-12 rounded-xl border-[#E5E5EA] focus:border-[#0071e3] focus:ring-[#0071e3]"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1D1D1F]">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="mario.rossi@email.it"
                        value={formData.email}
                        onChange={handleChange}
                        className="h-12 rounded-xl border-[#E5E5EA] focus:border-[#0071e3] focus:ring-[#0071e3]"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1D1D1F]">Telefono</Label>
                      <Input
                        id="phone"
                        name="phone"
                        placeholder="+39 333 1234567"
                        value={formData.phone}
                        onChange={handleChange}
                        className="h-12 rounded-xl border-[#E5E5EA] focus:border-[#0071e3] focus:ring-[#0071e3]"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-[#1D1D1F]">Oggetto</Label>
                      <Input
                        id="subject"
                        name="subject"
                        placeholder="es. Informazioni su un immobile"
                        value={formData.subject}
                        onChange={handleChange}
                        className="h-12 rounded-xl border-[#E5E5EA] focus:border-[#0071e3] focus:ring-[#0071e3]"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#1D1D1F]">Messaggio *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Scrivi qui il tuo messaggio..."
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="rounded-xl border-[#E5E5EA] focus:border-[#0071e3] focus:ring-[#0071e3] resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="h-12 px-8 rounded-full bg-[#0071e3] hover:bg-[#0077ED] text-white font-medium"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Invia Messaggio
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* FAQ Section */}
            <div>
              <h2 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight mb-2">
                Domande frequenti
              </h2>
              <p className="text-[#86868B] mb-8">
                Le risposte alle domande più comuni.
              </p>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-[#F5F5F7] rounded-2xl p-6"
                  >
                    <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-[15px] text-[#86868B] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-[#1D1D1F] rounded-3xl text-white">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Building className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Sei un agente immobiliare?</h3>
                    <p className="text-[#A1A1A6] text-sm mb-4">
                      Scopri come diventare partner WEQASA e accedere a immobili esclusivi.
                    </p>
                    <Button 
                      variant="outline" 
                      className="rounded-full border-white/30 text-white hover:bg-white/10"
                      asChild
                    >
                      <a href="/registrati/agente">
                        Diventa Partner
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.06)]">
            <div className="h-80 bg-gradient-to-br from-[#E5E5EA] to-[#D1D1D6] flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-[#0071e3]" />
                </div>
                <p className="text-lg font-semibold text-[#1D1D1F]">Via Roma 123, Roma</p>
                <p className="text-[#86868B]">Sede centrale WEQASA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
